import styles from './maintenances-table.module.scss';
import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import { Pagination } from '@/services/request/request.types';
import { calculateDateFromISOtoYYYY_MM_DD, calculateHourFromISODate } from '@/utils/helpers';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Empty, Flex, Spin, Table, TablePaginationConfig } from 'antd';
import { ColumnsType, FilterValue, SorterResult } from 'antd/es/table/interface';
import { Link } from 'react-router-dom';
import { generateDataSource } from './maintenances-table.utils';
import { Maintenance } from '@/services/maintenances-service/maintenances-service.types';
import MaintenanceStatusChip from '../maintenance-status-chip/maintenance-status-chip';
import PriorityChip from '../priority-chip/priority-chip';
import { Button } from '../button/button';
import MaintenanceProperty from '../maintenance-property/maintenance-property';

interface MaintenanceTableProps {
  onChange: (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => void;
  maintenancesData?: Maintenance[];
  pagination?: Pagination;
  isAdmin?: boolean;
  onShowMaintenanceDetails: (id: number) => void;
  currentPage: number;
  loading?: boolean;
}

export const MaintenanceTable = ({
  onChange,
  maintenancesData,
  pagination,
  isAdmin,
  currentPage,
  onShowMaintenanceDetails,
  loading,
}: MaintenanceTableProps) => {
  const dataSource = generateDataSource(maintenancesData || []);

  const handleChange = (
    pagination: TablePaginationConfig,
    _: Record<string, FilterValue | null>,
    sorter: SorterResult<Maintenance> | SorterResult<Maintenance>[]
  ) => {
    if (!Array.isArray(sorter)) {
      sorter = [sorter];
    }

    const sort = sorter.find((s) => s.order);

    const sortBy = sort?.field?.toString();
    const orderBy = sort ? (sort.order === 'ascend' ? 'asc' : 'desc') : undefined;

    onChange(pagination.current, pagination.pageSize, sortBy, orderBy);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: ColumnsType<Maintenance> = [
    ...(isAdmin
      ? [
          {
            title: 'Company',
            dataIndex: 'company_name',
            key: 'company',
            className: styles.companyContainer,
            render: (company_name: Maintenance['company_name']) => <div>{company_name || ''}</div>,
          },
        ]
      : []),
    {
      title: 'Phone number',
      dataIndex: 'phone',
      key: 'phone',
      className: styles.phoneNumberContainer,
      render: (phone: Maintenance['phone'], record) => {
        return (
          <div>
            {record.conversation_id ? (
              <Link to={`/conversations/${record?.conversation_id}`} className={styles.phoneNumber}>
                {phone}
              </Link>
            ) : (
              <p className={styles.phoneNumberText}>{phone}</p>
            )}
          </div>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      className: styles.bodyContainer,
      render: (description: Maintenance['description']) =>
        !description ? <p>---</p> : <div>{description}</div>,
    },
    {
      title: 'Has property',
      dataIndex: 'has_property',
      key: 'has_property',
      className: styles.propertyContainer,
      render: (hasProperty: Maintenance['has_property']) => (
        <MaintenanceProperty hasProperty={hasProperty} />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      className: styles.statusContainer,
      render: (status: Maintenance['status']) => <MaintenanceStatusChip status={status} />,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      className: styles.priorityContainer,
      render: (priority: Maintenance['priority']) => <PriorityChip priority={priority} />,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      className: styles.dateAndTimeContainer,
      render: (created_at: Maintenance['created_at']) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(created_at)}</p>
            <span className={styles.circle}>&#9679;</span>
            <p className={styles.hour}>{calculateHourFromISODate(created_at)}</p>
          </Flex>
        </Flex>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      className: styles.dateAndTimeContainer,
      render: (updated_at: Maintenance['updated_at']) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(updated_at)}</p>
            <span className={styles.circle}>&#9679;</span>
            <p className={styles.hour}>{calculateHourFromISODate(updated_at)}</p>
          </Flex>
        </Flex>
      ),
    },
    {
      className: styles.actionContainer,
      render: (_, record: Maintenance) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <Button onClick={() => onShowMaintenanceDetails(record.id)} buttonVariant="secondary">
              Show
            </Button>
          </Flex>
        </Flex>
      ),
    },
  ];

  return (
    <Table
      pagination={{
        current: currentPage,
        position: ['bottomCenter'],
        pageSizeOptions: ['12', '25', '50'],
        showSizeChanger: true,
        defaultPageSize: pagination?.pageItems,
        total: pagination?.totalCount,
        locale: { items_per_page: '' },
        nextIcon: <Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />,
        prevIcon: <Icon component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />} />,
      }}
      onChange={handleChange}
      scroll={{ x: 800 }}
      className={styles.table}
      dataSource={dataSource}
      columns={columns}
      locale={{
        emptyText: loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <Spin indicator={<LoadingOutlined spin />} />
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ),
      }}
    />
  );
};
